/**
 * Institutional Portal — customer-facing tenant surface (Iteration 21C-C).
 *
 * Every backend call is tenant-scoped by `veralok/tenant.py`. Even if a
 * customer manually flips their session to reach `/admin`, the backend
 * refuses cross-tenant reads with 403 `tenant_forbidden`.
 */
import React from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { TenantRoute } from "./PortalGuards";
import { api } from "@/lib/api";
import VeraLokLogo from "@/marketing/VeraLokLogo";
import "@/marketing/design-system.css";

// Re-use existing pages for the sections that already exist.
import InstitutionDetail from "@/pages/InstitutionDetail";
import QRPage from "@/pages/QRPage";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import AuditLog from "@/pages/AuditLog";
import { Building2, Search, ClipboardList, Fingerprint, Users, Settings, ScrollText, Terminal, ShieldCheck, Cog, KeyRound, Webhook, ArrowLeftRight, HandCoins } from "lucide-react";
// Analytics removed from Institution Portal 2026-02-21 (platform boundary leak).
import Developer from "@/pages/Developer";
import SettingsPage from "@/pages/Settings";
import VerificationRequests from "./pages/VerificationRequests";
import ConsentOutcomes from "./pages/ConsentOutcomes";
import Compliance from "./pages/Compliance";
import Webhooks from "./pages/Webhooks";
import InstitutionDashboard from "./pages/InstitutionDashboard";
import InstitutionVerify from "./pages/InstitutionVerify";
import RequestVerification from "./pages/RequestVerification";
import Billing from "./pages/Billing";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import InstitutionTeam from "./pages/InstitutionTeam";
import DisclosurePortal from "./pages/DisclosurePortal";
import { PortalHeaderUserMenu } from "./PortalHeaderUserMenu";
import { DemoProvenanceRibbon, useIsDemoTenant } from "./DemoProvenance";

const SECTIONS = [
  { key: "dashboard", label: "Dashboard", path: "" },
  { key: "verify", label: "Scan & Verify", path: "verify" },
  { key: "request", label: "Request Verification", path: "request" },
  { key: "verifications", label: "Verification Requests", path: "verifications" },
  { key: "qr", label: "Institution QR", path: "qr" },
  { key: "cases", label: "Cases", path: "cases" },
  { key: "consents", label: "Consent Outcomes", path: "consents" },
  { key: "disclosure", label: "Secure Disclosure", path: "disclosure" },
  { key: "audit", label: "Audit", path: "audit" },
  { key: "team", label: "Team & Access", path: "team" },
  { key: "credentials", label: "API Credentials", path: "credentials" },
  { key: "webhooks", label: "Webhooks", path: "webhooks" },
  { key: "compliance", label: "Compliance", path: "compliance" },
  { key: "billing", label: "Billing", path: "billing" },
  { key: "settings", label: "Settings", path: "settings" },
];

// The 7-day Demo v2 trial gets a deliberately reduced set of nav
// items — the four surfaces a non-technical evaluator actually
// needs to experience VeraLok. Everything else is still reachable
// via direct URL if a technical evaluator asks for it, but it does
// not clutter the shell.
const DEMO_SECTION_KEYS = new Set([
  "dashboard", "verify", "request", "verifications", "audit",
]);

export default function InstitutionShell() {
  return (
    <TenantRoute>
      <div className="vlk-portal min-h-screen flex flex-col" data-testid="institution-shell">
        <DemoProvenanceRibbon />
        <div className="flex flex-1 min-h-0">
          <InstitutionSidebar />
          <main className="flex-1 min-w-0">
            <InstitutionHeaderStrip />
            <SuspensionBanner />
            <div className="p-6">
              <Routes>
                <Route path="" element={<InstitutionDashboard />} />
                <Route path="verify" element={<InstitutionVerify />} />
                <Route path="request" element={<RequestVerification />} />
                <Route path="verifications" element={<VerificationRequests />} />
                <Route path="qr" element={<QRPage />} />
                <Route path="cases" element={<Cases />} />
                <Route path="cases/:case_id" element={<CaseDetail />} />
                <Route path="consents" element={<ConsentOutcomes />} />
                <Route path="disclosure" element={<DisclosurePortal />} />
                <Route path="audit" element={<AuditLog />} />
                <Route path="team" element={<InstitutionTeam />} />
                <Route path="credentials" element={<Developer />} />
                <Route path="webhooks" element={<Webhooks />} />
                <Route path="compliance" element={<Compliance />} />
                <Route path="billing" element={<Billing />} />
                <Route path="billing/success" element={<BillingSuccess />} />
                <Route path="billing/cancel" element={<BillingCancel />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </TenantRoute>
  );
}

function InstitutionSidebar() {
  const isDemo = useIsDemoTenant();
  const visibleSections = isDemo
    ? SECTIONS.filter((s) => DEMO_SECTION_KEYS.has(s.key))
    : SECTIONS;
  return (
    <aside className="vlk-portal-sidebar w-60 p-4 space-y-1 shrink-0" data-testid="institution-sidebar">
      <div className="mb-4 flex items-center gap-2">
        <VeraLokLogo size={24} />
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 text-white/40">
        {isDemo ? "Demo v2 · 7-day trial" : "Institution"}
      </div>
      {visibleSections.map((s) => (
        <NavLink
          key={s.key}
          to={`/institution/${s.path}`}
          end={s.path === ""}
          className={({ isActive }) =>
            `vlk-portal-nav-link ${isActive ? "active" : ""}`
          }
          data-testid={`institution-nav-${s.key}`}
        >
          {s.label}
        </NavLink>
      ))}
    </aside>
  );
}

function InstitutionHeaderStrip() {
  const { user } = useAuth();
  return (
    <div className="vlk-portal-header px-6 py-3 flex items-center justify-between text-xs" data-testid="institution-header-strip">
      <div className="text-white/60 flex items-center gap-3">
        <VeraLokLogo size={22} showWordmark={false} />
        <span className="font-semibold text-white/80">Institution Portal</span>
        <span className="text-white/30">·</span>
        <span className="text-white/50 hidden md:inline" data-testid="institution-header-descriptor">Verify · Consent · Audit</span>
        <span className="text-white/30 hidden md:inline">·</span>
        <span className="vlk-portal-role-pill" data-testid="institution-tenant-id">
          {user?.institution_id || "unbound"}
        </span>
      </div>
      <div className="text-white/70 flex items-center gap-3">
        <PortalHeaderUserMenu variant="institution" testIdPrefix="institution" />
      </div>
    </div>
  );
}

/**
 * SuspensionBanner — Session B ecosystem propagation.
 *
 * When VeraLok compliance suspends this tenant, the API layer already
 * returns 423 `institution_suspended` on operational endpoints (verify,
 * QR create, key rotate, webhook create, etc.). This banner surfaces
 * the "why" and the "when" so the tenant admin does not stare at
 * confusing 423s in the browser network tab.
 *
 * Reads: `GET /api/institutions/{own_id}` — the same endpoint the
 * dashboard uses; no new call.
 * Never shown for internal (system_admin) users impersonating a
 * tenant since they have no `institution_id` bound.
 */
function SuspensionBanner() {
  const { user } = useAuth();
  const instId = user?.institution_id;
  const { data } = useQuery({
    queryKey: ["institution-self-status", instId],
    queryFn: () => api.get(`/institutions/${instId}`).then((r) => r.data),
    enabled: !!instId,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  if (!instId || !data || data.status !== "SUSPENDED") return null;
  return (
    <div
      className="border-l-4 border-red-500 bg-red-500/10 text-red-100 px-6 py-3 text-sm flex items-start gap-3"
      data-testid="institution-suspension-banner"
      role="alert"
    >
      <span className="font-semibold uppercase tracking-widest text-[11px] mt-0.5">
        Suspended
      </span>
      <div className="flex-1 min-w-0">
        <div>
          Your institution ({data.inst_vlk_id_display || data.vlk_inst_id})
          has been suspended by VeraLok compliance. Operational APIs
          (verify, QR issue, credential rotate, webhook writes) are
          disabled until reinstatement.
        </div>
        {data.suspend_reason && (
          <div className="mt-1 text-xs opacity-90">
            <span className="opacity-70">Reason:</span> {data.suspend_reason}
            {data.suspended_at && (
              <span className="opacity-60"> · since {new Date(data.suspended_at).toLocaleString()}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


